// Content of the Green Filter brochure (2026 CD flyer): racing story, proof numbers, how the filter
// works, "Keeping It Green", trade memberships and the mailing address. Same wording as the CMS
// (Website > Racing & Technology Story) so both sites say the same thing.

export const RACING = {
  eyebrow: 'Proven at Le Mans',
  heading: "Racing Isn't Just Our Passion. It's Our Proving Ground.",
  text: "Long before we manufactured a single air filter for your street-legal machine, we produced hundreds for the international motorsports industry. Green High Performance Air Filters are used exclusively by some of the world's most formidable on- and off-road racing teams, including Le Mans GT and World Rally Championship title holders. Winning teams know that when you need to get the most out of your engine, you've got to go Green!",
  highlight: 'Ford Wins 24 Hours of Le Mans with Green Filter USA',
  tagline: "We're Going Green!™",
  alt: 'Ford GT race car at night, winner of the 24 Hours of Le Mans with Green Filter USA',
  buttonLabel: 'Read our story',
  buttonLink: '/about-us/',
  image: '/assets/flyer/le-mans-ford-gt-front.webp',
} as const;

export const PROOF = {
  heading: 'Better Mileage. More Power. Cleaner Air.',
  lead: 'Test results for Green High Performance Air Filters compared with stock paper air filters.',
  stats: [
    {
      value: '5.6%',
      label: 'Better fuel mileage',
      text: "Increased air flow to your cylinders results in a more potent air/fuel mixture. In addition to the performance enhancements Green Air Filters offer, you'll also reap the benefits of increased fuel mileage.",
    },
    {
      value: '6.7%',
      label: 'More horsepower',
      text: 'Dynamometer bench testing on an 1800cc 4-cylinder engine showed that a Green High Performance Air Filter provided a 6.7% increase in horsepower over the stock paper air filter.',
    },
    {
      value: 'Up to 25%',
      label: 'More air flow',
      text: 'Our advanced production process virtually eliminates seepage into the woven cotton filtration surface, which increases air flow by as much as 25% vs. competing products.',
    },
    {
      value: '5 microns',
      label: 'Dirt particles trapped',
      text: 'Two layers of tightly interwoven cotton trap harmful dirt particles as small as 5 microns, so you get better airflow without sacrificing filtration.',
    },
  ],
  dyno: {
    title: 'Peak test at 5,500 RPM',
    greenHp: 118,
    stockHp: 110,
    note: 'Dynamometer bench testing on an 1800cc 4-cylinder engine.',
  },
} as const;

export const TECH_HEADING = 'How a Green Filter Works';

export const TECH_LEAD =
  'Race-proven design, double-layer cotton and a precision fit: here is what makes a Green High Performance Air Filter different from a disposable paper filter.';

export const TECH_SECTIONS: {
  title: string;
  text: string;
  points?: { title: string; text: string }[];
  image: string;
  alt: string;
}[] = [
  {
    title: 'Let It Breathe.',
    text: "Clean, free-flowing air is as critical to your engine's performance as the octane of your fuel. Reduce the restrictions in your intake system, and you'll increase the output of your engine. Green High Performance Air Filters are engineered to maximize air flow while trapping harmful dirt particles. The result of this race-proven technology is increased horsepower, improved throttle response, better fuel mileage, and greater torque throughout your engine's RPM range.",
    points: [
      {
        title: 'Precision Production',
        text: 'At Green Filter USA we use an advanced production process that virtually eliminates seepage into the woven cotton filtration surface, which increases air flow by as much as 25% vs. competing products.',
      },
      {
        title: 'Fit Like a Glove',
        text: 'Every filter we manufacture starts with a precision CNC-machined mold, designed, milled and tested in-house to ensure an airtight, factory-quality fit with no modifications necessary.',
      },
      {
        title: 'Pleats of Strength',
        text: 'Deep pleats in the double-layered cotton create a 10–20% increase in filtration surface. This allows a greater volume of air to move freely into your engine cylinders.',
      },
      {
        title: 'Greater Air Flow',
        text: 'In a side-by-side test bench comparison, Green High Performance Air Filters allowed greater air flow than paper filters.',
      },
    ],
    alt: 'Close-up of a Green panel air filter showing its deep cotton pleats',
    image: '/assets/flyer/filter-closeup.webp',
  },
  {
    title: 'Ionic Charge. Bionic Performance.',
    text: "Green High Performance Air Filters come pretreated with a specially formulated dirt-trapping oil. This oil creates a positive (+) magnetic charge, or ionic bond, with the filter's coated steel mesh. The positive charge attracts the negatively (-) charged dust particles entering the filter, causing them to cling to the steel mesh and allowing the cotton to remain clear for maximum air flow.",
    alt: 'Diagram of air flowing through a Green cone filter',
    image: '/assets/flyer/ionic-charge.webp',
  },
  {
    title: 'The Best Use of Cotton Since the Invention of Blue Jeans.',
    text: "Because of its unique properties and organic composition, cotton is the ideal material for use in high performance air filters. But beyond the mere use of the material, Green High Performance Air Filters are uniquely designed, engineered, and manufactured to take full advantage of cotton's capabilities. Unlike multi-layer gauze filters that can fray and release fiber particles into your engine, Green uses two layers of tightly interwoven cotton fabric. Cross-weaving one fine and one medium layer produces a fabric that is thinner and stronger than other materials, while maintaining the ability to trap harmful dirt particles as small as 5 microns. The result is better airflow without sacrificing filtration. And because the fabric is so durable, it can be washed and reused over and over.",
    alt: 'Double-layer woven cotton filter media',
    image: '/assets/features/img0.webp',
  },
  {
    title: 'Drop It In. Fire It Up.',
    text: "Green High Performance Air Filters come pre-oiled, so they're ready to use right out of the box. Pop open your air filter box, remove your stock filter and drop in a Green High Performance Air Filter. You simply can't perform a simpler, less expensive upgrade that yields as much improvement in engine performance.",
    points: [
      {
        title: 'Anyone Can Go Green.',
        text: 'Green High Performance Air Filters are available for virtually every domestic and import car, light truck, and SUV. All Green Filter air filters are covered by a limited lifetime guarantee.',
      },
      {
        title: 'Keep It Clean',
        text: 'Be sure to use only the Green Filter cleaner and oil kit. Others will discolor your filter.',
      },
    ],
    alt: 'Green Filter panel, cone and round air filters',
    image: '/assets/flyer/product-lineup.webp',
  },
];

export const KEEPING_GREEN = {
  heading: 'Keeping It Green.',
  text: "Change your air filter according to your manufacturer's recommended maintenance schedule, and you'll dump 20 paper air filters into our nation's landfills in the first 100,000 miles of driving.* But replace that stock filter with a washable, reusable Green High Performance Air Filter, and it'll be the last one you need. Add the benefits of reduced fuel consumption, and not only will you be helping the environment, you'll be putting a lot of Green back into your pocket.",
  note: '* Based on recommended air filter replacement every 4,000–5,000 miles, or with every oil change.',
} as const;

export const FOOTER_BADGES = [
  {
    name: 'PWA member (Performance Warehouse Association)',
    src: '/assets/flyer/badge-pwa.webp',
  },
  {
    name: 'SEMA member (Specialty Equipment Market Association)',
    src: '/assets/flyer/badge-sema.webp',
  },
  {
    name: 'Made in USA',
    src: '/assets/USA-Flag.webp',
  },
];

export const MAILING_ADDRESS = ['Green Filter USA', 'P.O. Box 150', 'Mt. Braddock, PA 15465'];
